import { ref } from 'vue'

/**
 * One app-styled confirm dialog, awaited like window.confirm.
 *
 *   if (await confirm({ title: 'Delete entry', message: '…' })) { … }
 *
 * State is module-level and App.vue renders the single modal-spinner that shows
 * it, so a page can ask a question without owning any markup. Everything is
 * rendered as plain text.
 */
export interface ConfirmRequest {
  title: string
  /** The question. Plain text; "\n" breaks a line. */
  message: string
  /** The subject of the question, shown on the line under it. */
  detail?: string
  /** Yes performs a destructive action, such as a delete, so it renders as a danger button. */
  destructive?: boolean
}

const visible = ref(false)
const title = ref('')
const message = ref('')
const detail = ref('')
const destructive = ref(false)

/** Resolves the promise handed to the caller of confirm(). */
let settle: ((confirmed: boolean) => void) | null = null

export function confirm(request: ConfirmRequest): Promise<boolean> {
  // A second question while one is still open would strand the first caller
  // waiting forever. Treat the interrupted one as declined.
  settle?.(false)

  title.value = request.title
  message.value = request.message
  detail.value = request.detail ?? ''
  destructive.value = request.destructive ?? false
  visible.value = true

  return new Promise<boolean>((resolve) => {
    settle = resolve
  })
}

/** For App.vue, which renders the dialog. Nothing else should need this. */
export function useConfirmDialog() {
  function close(confirmed: boolean) {
    visible.value = false
    settle?.(confirmed)
    settle = null
  }

  return {
    visible,
    title,
    message,
    detail,
    destructive,
    accept: () => close(true),
    dismiss: () => close(false),
  }
}
